/**
 * SOLOMON — RESEARCH PANEL
 * Persistent left-side panel: research task scheduler.
 * Lists active/paused/completed tasks; creates new via inline form;
 * per-task pause/resume/cancel controls.
 *
 * Smart auto-open: on mount, if any tasks are 'active' and the panel
 * is currently closed, calls onAutoOpen() to request opening without
 * persisting to localStorage (user's explicit preference still wins
 * on subsequent sessions with no active tasks).
 *
 * Create flow: POST returns a pending action (Phase 2A guardrail).
 * User approves via ActionCard in chat; next poll surfaces the task.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useSolomon } from '../../context/SolomonContext';
import { ActionCard } from '../ActionCard';
import { latexToPlainText } from '../../utils/latexNormalize';
import { classifyLastError } from './lastErrorBadge.js';
import './researchPanel.css';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';
const POLL_INTERVAL_MS = 60_000;

const DEFAULT_FORM = {
  topic: '',
  scope: '',
  depth: 'advanced',
  duration: 3,
  frequency: 6,
};

function BudgetLine({ budget }) {
  if (!budget?.pools) return null;
  const r = budget.pools.research || {};
  const c = budget.pools.chat || {};
  // resetsOn is a date-only string ("2026-10-01"); `new Date(str)` parses that as
  // UTC midnight, which renders as the previous day in any timezone behind UTC.
  // Build the Date from local parts instead.
  const [resetY, resetM, resetD] = String(budget.period?.resetsOn).split('-').map(Number);
  const resets = (resetY && resetM && resetD)
    ? new Date(resetY, resetM - 1, resetD).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    : 'the 1st';
  return (
    <div className="research-task-meta research-budget-line" title="Tavily search credits this month, by pool">
      {budget.exhausted
        ? `Search credits spent for this month · resets ${resets}`
        : `Search credits · research ${r.remaining}/${r.limit} · chat ${c.remaining}/${c.limit} · resets ${resets}`}
    </div>
  );
}

export default function ResearchPanel({ isOpen, onToggle, onAutoOpen }) {
  const { authFetch } = useAuth();
  const {
    pendingActions,
    addPendingAction,
    approveAction:    approveActionCtx,
    cancelAction:     cancelActionCtx,
  } = useSolomon();

  const [tasks, setTasks]                 = useState([]);
  const [loading, setLoading]             = useState(true);
  const [fetchError, setFetchError]       = useState(null);
  const [formOpen, setFormOpen]           = useState(false);
  const [form, setForm]                   = useState(DEFAULT_FORM);
  const [submitting, setSubmitting]       = useState(false);
  const [submitError, setSubmitError]     = useState(null);
  const [submitStatus, setSubmitStatus]   = useState(null);
  const [localActionId, setLocalActionId] = useState(null);
  const [cancelConfirmId, setCancelConfirmId] = useState(null);
  const [cardError, setCardError]         = useState({});
  const [expanded, setExpanded]             = useState(() => new Set());
  const [findingsByTask, setFindingsByTask] = useState({});
  const [quickPollDeadline, setQuickPollDeadline] = useState(null);
  const [budget, setBudget] = useState(null);
  const pendingQuickModeRef = useRef(false);
  const hasAutoOpenedRef = useRef(false);

  // ── Fetch tasks ─────────────────────────────────────────────
  const fetchTasks = useCallback(async (options = {}) => {
    const { triggerAutoOpen = false } = options;
    try {
      const res = await authFetch(`${API_BASE}/api/research/tasks`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const list = data.data?.tasks || [];
      setTasks(list);
      setFindingsByTask({});  // Clear findings cache on each list refresh (simple correctness).
      setFetchError(null);

      // Search-credit ledger (Issue 102 §11). Fail-open: a missing budget hides the line.
      try {
        const b = await authFetch(`${API_BASE}/api/research/budget`);
        if (b.ok) { const bj = await b.json(); setBudget(bj.data || null); }
      } catch { /* line simply absent */ }

      // Smart auto-open — fires at most once per mount. Only opens if
      // currently closed AND there's at least one active task. Does NOT
      // write to localStorage; the user's explicit preference stays intact.
      if (triggerAutoOpen && !hasAutoOpenedRef.current && !isOpen && onAutoOpen) {
        if (list.some(t => t.status === 'active')) {
          onAutoOpen();
        }
        hasAutoOpenedRef.current = true;
      }
    } catch (err) {
      console.warn('[Solomon/Research] fetch failed:', err.message);
      setFetchError("Couldn't load tasks.");
    } finally {
      setLoading(false);
    }
  }, [authFetch, isOpen, onAutoOpen]);

  // Initial mount fetch (always, regardless of isOpen — needed for auto-open check).
  useEffect(() => {
    fetchTasks({ triggerAutoOpen: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll every 60s while open + refresh on open.
  useEffect(() => {
    if (!isOpen) return;
    fetchTasks();
    const id = setInterval(() => fetchTasks(), POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [isOpen, fetchTasks]);

  // Quick-mode fast poll: after approval, poll every 5s until the deadline
  // passes (60s window). Runs regardless of panel open state so findings
  // populate even if the user collapses the panel temporarily.
  useEffect(() => {
    if (!quickPollDeadline) return;
    const id = setInterval(() => {
      if (Date.now() > quickPollDeadline) {
        setQuickPollDeadline(null);
        return;
      }
      fetchTasks();
    }, 5_000);
    return () => clearInterval(id);
  }, [quickPollDeadline, fetchTasks]);

  // ── Create ──────────────────────────────────────────────────
  const handleCreate = async (e, mode = 'scheduled') => {
    e?.preventDefault?.();
    if (!form.topic.trim()) {
      setSubmitError('Topic is required');
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      // Quick mode fixes duration/frequency so the one-shot cycle behaves
      // predictably regardless of current form values.
      const body = mode === 'quick' ? {
        topic:       form.topic.trim(),
        scope:       form.scope.trim() || null,
        depth:       form.depth,
        duration:    1,
        frequency:   1,
        executionMode: 'quick',
      } : {
        topic:       form.topic.trim(),
        scope:       form.scope.trim() || null,
        depth:       form.depth,
        duration:    Number(form.duration)    || 3,
        frequency:   Number(form.frequency)   || 6,
        executionMode: 'scheduled',
      };

      const res = await authFetch(`${API_BASE}/api/research/task`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);

      // Dispatch the pending action into global state and track its ID
      // locally so we can render an inline ActionCard below. Mirrors the
      // pattern proven in the (since-deleted) ComposeEmail — kept as the reference shape.
      const action = data.data?.action;
      if (action?.status === 'pending') {
        pendingQuickModeRef.current = (mode === 'quick');
        addPendingAction(action);
        setLocalActionId(action.id);
        setSubmitStatus(mode === 'quick'
          ? '✓ Quick research proposed — approve to run once'
          : '✓ Proposed — approve below');
      } else if (action?.status === 'rejected' || action?.status === 'queued') {
        setSubmitError(action.reason || `Research task ${action.status}`);
        return;
      } else {
        setSubmitStatus('✓ Proposed for approval');
      }

      setForm(DEFAULT_FORM);
      setFormOpen(false);
      fetchTasks();
    } catch (err) {
      setSubmitError(err.message || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Approve / cancel the inline pending action ──────────────
  const handleApproveLocal = useCallback(async (actionId) => {
    try {
      await approveActionCtx(actionId);
      setLocalActionId(null);
      const wasQuick = pendingQuickModeRef.current;
      setSubmitStatus(wasQuick ? '✓ Approved — running now…' : '✓ Approved — creating task…');
      if (wasQuick) {
        // Fast-poll window: user just approved a one-shot cycle. Default
        // poll is 60s — bump to 5s for 60s so findings + summary populate
        // visibly without waiting a full poll cycle.
        setQuickPollDeadline(Date.now() + 60_000);
      }
      pendingQuickModeRef.current = false;
      fetchTasks();
    } catch (err) {
      setSubmitError(err.message || 'Approval failed');
    }
  }, [approveActionCtx, fetchTasks]);

  const handleCancelLocal = useCallback(async (actionId) => {
    try {
      await cancelActionCtx(actionId);
      setLocalActionId(null);
      setSubmitStatus('Cancelled');
    } catch (err) {
      setSubmitError(err.message || 'Cancel failed');
    }
  }, [cancelActionCtx]);

  // ── Per-task actions (pause/resume/cancel) ──────────────────
  const callAction = async (taskId, action) => {
    setCardError(prev => {
      const next = { ...prev };
      delete next[taskId];
      return next;
    });
    try {
      const res = await authFetch(`${API_BASE}/api/research/task/${taskId}/${action}`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);
      fetchTasks();
    } catch (err) {
      setCardError(prev => ({ ...prev, [taskId]: err.message || `Failed to ${action}` }));
    }
  };

  const handlePauseResume = (task) => {
    if (task.status === 'active') return callAction(task.id, 'pause');
    if (task.status === 'paused') return callAction(task.id, 'resume');
  };

  const handleCancelConfirm = (taskId) => {
    callAction(taskId, 'cancel');
    setCancelConfirmId(null);
  };

  // ── Findings (lazy per-task fetch) ──────────────────────────
  const fetchFindings = useCallback(async (taskId) => {
    setFindingsByTask(prev => ({ ...prev, [taskId]: { loading: true, findings: [], error: null } }));
    try {
      const res = await authFetch(`${API_BASE}/api/research/task/${taskId}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const findings = data.data?.findings || [];
      setFindingsByTask(prev => ({ ...prev, [taskId]: { loading: false, findings, error: null } }));
    } catch (err) {
      setFindingsByTask(prev => ({ ...prev, [taskId]: { loading: false, findings: [], error: err.message || 'Failed to load' } }));
    }
  }, [authFetch]);

  const handleCardClick = (task, e) => {
    if (e.target.closest('button, a')) return;  // guard transport/confirm/links
    const wasExpanded = expanded.has(task.id);
    setExpanded(prev => {
      const next = new Set(prev);
      if (wasExpanded) next.delete(task.id); else next.add(task.id);
      return next;
    });
    if (!wasExpanded && findingsByTask[task.id] === undefined) {
      fetchFindings(task.id);
    }
  };

  // ── Form helpers ────────────────────────────────────────────
  const openForm = () => {
    setForm(DEFAULT_FORM);
    setSubmitError(null);
    setFormOpen(true);
  };
  const closeForm = () => {
    setFormOpen(false);
    setForm(DEFAULT_FORM);
    setSubmitError(null);
  };

  // ── Render ──────────────────────────────────────────────────
  return (
    <>
      {isOpen && <div className="research-panel-backdrop" onClick={onToggle} />}

      <aside
        className={`research-panel ${isOpen ? 'research-panel--open' : 'research-panel--closed'}`}
        aria-hidden={!isOpen}
      >
        <div className="research-panel-header">
          <h2 className="research-panel-title">Research</h2>
          <div className="research-panel-header-actions">
            <button
              className="research-panel-refresh"
              onClick={() => fetchTasks()}
              title="Refresh task list"
              aria-label="Refresh task list"
            >
              <RefreshIcon />
            </button>
            <button
              className="research-panel-close"
              onClick={onToggle}
              title="Collapse research panel"
              aria-label="Collapse research panel"
            >×</button>
          </div>
        </div>

        <div className="research-panel-body">
          <button
            className="sol-btn sol-btn--glass-primary research-panel-new-btn"
            onClick={formOpen ? closeForm : openForm}
            aria-expanded={formOpen}
          >
            {formOpen ? '× Cancel' : '+ New Research Task'}
          </button>

          {submitStatus && (
            <div className="research-panel-toast">{submitStatus}</div>
          )}

          {/* Inline ActionCard for our own proposed-but-not-approved action.
              Mirrors the (since-deleted) ComposeEmail pattern — panel owns its pending action
              by id, finds it in context.pendingActions, renders here so the
              user has a local approve surface instead of a phantom chat one. */}
          {localActionId && (() => {
            const localAction = pendingActions.find(a => a.id === localActionId);
            return localAction ? (
              <div className="research-panel-action-slot">
                <ActionCard
                  action={localAction}
                  onApprove={handleApproveLocal}
                  onCancel={handleCancelLocal}
                />
              </div>
            ) : null;
          })()}

          {formOpen && (
            <form className="research-form" onSubmit={handleCreate}>
              <label className="research-form-label">
                Topic
                <input
                  type="text"
                  className="research-form-input"
                  value={form.topic}
                  onChange={e => setForm(f => ({ ...f, topic: e.target.value }))}
                  placeholder="e.g. AI coding agents 2026"
                  required
                  autoFocus
                />
              </label>
              <label className="research-form-label">
                Scope <span className="research-form-optional">(optional)</span>
                <textarea
                  className="research-form-textarea"
                  value={form.scope}
                  onChange={e => setForm(f => ({ ...f, scope: e.target.value }))}
                  placeholder="Focus areas, specific angles…"
                  rows={2}
                />
              </label>
              <div className="research-form-row">
                <label className="research-form-label research-form-label--third">
                  Depth
                  <select
                    className="research-form-input"
                    value={form.depth}
                    onChange={e => setForm(f => ({ ...f, depth: e.target.value }))}
                  >
                    <option value="ultra-fast">Ultra-Fast</option>
                    <option value="fast">Fast</option>
                    <option value="basic">Basic</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </label>
                <label className="research-form-label research-form-label--third">
                  Days
                  <input
                    type="number" min="1" max="30"
                    className="research-form-input"
                    value={form.duration}
                    onChange={e => setForm(f => ({ ...f, duration: e.target.value }))}
                  />
                </label>
                <label className="research-form-label research-form-label--third">
                  Every (hrs)
                  <input
                    type="number" min="1" max="168"
                    className="research-form-input"
                    value={form.frequency}
                    onChange={e => setForm(f => ({ ...f, frequency: e.target.value }))}
                  />
                </label>
              </div>
              {submitError && (
                <div className="research-form-error">{submitError}</div>
              )}
              <div className="research-form-actions">
                <button
                  type="button"
                  className="research-form-btn research-form-btn--ghost"
                  onClick={closeForm}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="research-form-btn research-form-btn--secondary"
                  onClick={(e) => handleCreate(e, 'quick')}
                  disabled={submitting || !form.topic.trim()}
                  title="Run once now instead of scheduling recurring cycles"
                >
                  {submitting ? '…' : 'Run Once'}
                </button>
                <button
                  type="submit"
                  className="research-form-btn research-form-btn--primary"
                  disabled={submitting || !form.topic.trim()}
                >
                  {submitting ? 'Submitting…' : 'Propose Task'}
                </button>
              </div>
            </form>
          )}

          {loading && tasks.length === 0 && (
            <div className="research-panel-empty">
              <p>Loading…</p>
            </div>
          )}

          {fetchError && (
            <div className="research-panel-error">
              {fetchError}
              <button onClick={() => fetchTasks()}>Retry</button>
            </div>
          )}

          {!loading && !fetchError && tasks.length === 0 && !formOpen && (
            <div className="research-panel-empty">
              <p>No research tasks yet.</p>
              <p className="research-panel-empty-hint">
                Click <strong>+ New Research Task</strong> above to schedule one.
              </p>
            </div>
          )}

          <BudgetLine budget={budget} />

          {tasks.map(task => (
            <TaskCard
              key={task.id}
              task={task}
              expanded={expanded.has(task.id)}
              findingsEntry={findingsByTask[task.id]}
              onCardClick={(e) => handleCardClick(task, e)}
              onPauseResume={() => handlePauseResume(task)}
              onCancel={() => setCancelConfirmId(task.id)}
              cancelConfirmOpen={cancelConfirmId === task.id}
              onCancelConfirm={() => handleCancelConfirm(task.id)}
              onCancelDismiss={() => setCancelConfirmId(null)}
              error={cardError[task.id]}
            />
          ))}
        </div>
      </aside>
    </>
  );
}

// ── Task card ─────────────────────────────────────────────────
function TaskCard({
  task,
  expanded,
  findingsEntry,
  onCardClick,
  onPauseResume,
  onCancel,
  cancelConfirmOpen,
  onCancelConfirm,
  onCancelDismiss,
  error,
}) {
  const {
    status,
    topic,
    depth,
    execution_mode,
    searches_used = 0,
    started_at,
    expires_at,
    findings_count = 0,
    last_run_at,
    last_error,
    last_error_at,
  } = task;

  // Issue 102 §11: no search cap — progress is elapsed time to expires_at.
  const startMs = started_at ? new Date(started_at).getTime() : Date.now();
  const endMs   = expires_at ? new Date(expires_at).getTime() : startMs;
  const progress = endMs > startMs ? Math.min(100, Math.max(0, ((Date.now() - startMs) / (endMs - startMs)) * 100)) : 100;
  const showProgress  = status === 'active' || status === 'paused';
  const showTransport = status === 'active' || status === 'paused';
  const errorBadge    = classifyLastError(last_error);

  return (
    <div
      className={`research-task research-task--${status}${expanded ? ' research-task--expanded' : ''}`}
      onClick={onCardClick}
      role="button"
      tabIndex={0}
    >
      <div className="research-task-title-row">
        <ChevronIcon className="research-task-caret" />
        <div className="research-task-title">{topic}</div>
      </div>
      <div className="research-task-meta-row">
        <span className={`research-task-dot research-task-dot--${status}`} />
        <span className="research-task-status">{status}</span>
        {execution_mode === 'quick' && <span className="research-task-mode-badge">Quick</span>}
        {depth && <span className="research-task-depth">{depth}</span>}
        {errorBadge && (
          <span
            className={errorBadge.kind === 'error' ? 'research-task-error-badge' : 'research-task-note-badge'}
            title={`${last_error}${last_error_at ? '\n' + new Date(last_error_at).toLocaleString() : ''}`}
          >
            {errorBadge.label}
          </span>
        )}
      </div>

      {showProgress && (
        <div className="research-task-progress-wrap">
          <div className="research-task-progress">
            <div className="research-task-progress-bar" style={{ width: `${progress}%` }} />
          </div>
          <div className="research-task-progress-label">
            {searches_used} searches · until {expires_at ? new Date(expires_at).toLocaleDateString() : '—'}
          </div>
        </div>
      )}

      <div className="research-task-footer">
        <span className="research-task-meta">
          {formatRelativeTime(last_run_at)}
          {findings_count > 0 && ` · ${findings_count} findings`}
        </span>

        {!cancelConfirmOpen && showTransport && (
          <div className="research-task-actions">
            <button
              className="research-task-btn"
              onClick={onPauseResume}
              title={status === 'active' ? 'Pause task' : 'Resume task'}
              aria-label={status === 'active' ? 'Pause task' : 'Resume task'}
            >
              {status === 'active' ? <PauseIcon /> : <PlayIcon />}
            </button>
            <button
              className="research-task-btn research-task-btn--danger"
              onClick={onCancel}
              title="Cancel task"
              aria-label="Cancel task"
            >
              <XIcon />
            </button>
          </div>
        )}

        {cancelConfirmOpen && (
          <div className="research-task-confirm">
            <span className="research-task-confirm-label">Cancel?</span>
            <button
              className="research-task-confirm-btn research-task-confirm-btn--yes"
              onClick={onCancelConfirm}
            >Yes</button>
            <button
              className="research-task-confirm-btn research-task-confirm-btn--no"
              onClick={onCancelDismiss}
            >No</button>
          </div>
        )}
      </div>

      {error && <div className="research-task-error">{error}</div>}

      {expanded && <FindingsSection entry={findingsEntry} summary={task.summary} />}
    </div>
  );
}

// ── Findings section ─────────────────────────────────────────
function FindingsSection({ entry, summary }) {
  const summarySection = summary ? (
    <div className="research-summary">
      <div className="research-summary-header">Summary</div>
      <div className="research-summary-body">{latexToPlainText(summary)}</div>
    </div>
  ) : null;

  if (!entry || entry.loading) {
    return (
      <div className="research-findings">
        {summarySection}
        <div className="research-findings-header">Findings …</div>
      </div>
    );
  }
  if (entry.error) {
    return (
      <div className="research-findings">
        {summarySection}
        <div className="research-findings-header">Findings</div>
        <div className="research-findings-error">Couldn't load findings.</div>
      </div>
    );
  }
  const { findings } = entry;
  return (
    <div className="research-findings">
      {summarySection}
      <div className="research-findings-header">Findings ({findings.length})</div>
      {findings.length === 0 ? (
        <div className="research-findings-empty">No findings yet. Check back after the next cycle.</div>
      ) : (
        <div className="research-findings-list">
          {findings.map(f => <FindingRow key={f.id} finding={f} />)}
        </div>
      )}
    </div>
  );
}

function FindingRow({ finding }) {
  const { source_url, source_title, content, created_at } = finding;
  // `summary` is a static tag ("Research: ${topic}") on every finding — skip.
  // Backend sanitizer currently stores `${title}\n${content}` in the content
  // field (executeResearchCycle in researchManager.js concatenates before
  // sanitizing), so strip a duplicated leading title line when present.
  let body = latexToPlainText((content || '').trim());
  if (source_title && body.startsWith(source_title)) {
    body = body.slice(source_title.length).trim();
  }
  const excerpt = body.length > 160 ? body.slice(0, 160).trimEnd() + '…' : body;
  const host = (() => {
    if (!source_url) return null;
    try { return new URL(source_url).host.replace(/^www\./, ''); } catch { return null; }
  })();
  const time = formatRelativeTime(created_at);
  const title = source_title || source_url || 'Untitled finding';
  return (
    <div className="research-finding">
      {source_url ? (
        <a className="research-finding-title" href={source_url} target="_blank" rel="noopener noreferrer">{title}</a>
      ) : (
        <span className="research-finding-title research-finding-title--plain">{title}</span>
      )}
      {excerpt && <div className="research-finding-excerpt">{excerpt}</div>}
      <div className="research-finding-meta">
        {host && <span>{host}</span>}
        {host && time && <span>·</span>}
        {time && <span>{time}</span>}
      </div>
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────
function formatRelativeTime(iso) {
  if (!iso) return 'Never run yet';
  const then = new Date(iso);
  if (isNaN(then.getTime())) return '';
  const diffMs = Date.now() - then.getTime();
  const min = 60_000, hr = 3600_000, day = 86_400_000;
  if (diffMs < min) return 'Just now';
  if (diffMs < hr)  return `${Math.floor(diffMs / min)}m ago`;
  if (diffMs < day) return `${Math.floor(diffMs / hr)}h ago`;
  return `${Math.floor(diffMs / day)}d ago`;
}

// ── Icons ──────────────────────────────────────────────────────

// Exported — used by ChatInterface header cluster.
export function FlaskIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 3v6l-5 9a2 2 0 0 0 1.8 3h12.4a2 2 0 0 0 1.8-3l-5-9V3" />
      <line x1="9" y1="3" x2="15" y2="3" />
      <line x1="7" y1="13" x2="17" y2="13" />
    </svg>
  );
}

function RefreshIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  );
}

function PauseIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
      <rect x="6"  y="4" width="4" height="16" />
      <rect x="14" y="4" width="4" height="16" />
    </svg>
  );
}

function PlayIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
      <polygon points="5,3 21,12 5,21" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6"  y1="6" x2="18" y2="18" />
    </svg>
  );
}

function ChevronIcon({ className }) {
  return (
    <svg className={className} width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}
